import React, { useState, useEffect, useRef } from 'react';
import { useSearch, SearchResult } from '../../contexts/SearchContext';
import { useLocation } from 'wouter';
import { Search, X, ArrowRight, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

const SearchModal = () => {
  const { 
    searchQuery, 
    setSearchQuery, 
    searchResults, 
    isSearchOpen, 
    closeSearch,
    loading
  } = useSearch();

  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsContainerRef = useRef<HTMLDivElement>(null);
  const selectedResultRef = useRef<HTMLAnchorElement>(null);

  // Focus input when modal opens
  useEffect(() => {
    if (isSearchOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isSearchOpen]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [searchResults]);

  // Scroll selected result into view
  useEffect(() => {
    if (selectedIndex >= 0 && selectedResultRef.current && resultsContainerRef.current) {
      selectedResultRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'nearest' 
      });
    }
  }, [selectedIndex]);

  const [, navigate] = useLocation();
  
  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => 
        prev < searchResults.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      const selectedResult = searchResults[selectedIndex];
      if (selectedResult) {
        closeSearch();
        
        // Check if it's an external link
        if (selectedResult.isExternalLink) {
          // Open external link in a new tab
          window.open(selectedResult.url, '_blank', 'noopener,noreferrer');
          return;
        }
        
        // Handle internal navigation
        // Parse the URL to extract path and possible section ID
        const url = new URL(selectedResult.url, window.location.origin);
        const path = url.pathname;
        const sectionId = url.hash ? url.hash.substring(1) : null;
        
        // Navigate to the page
        navigate(path);
        
        // After navigation, scroll to the section if specified
        setTimeout(() => {
          // Get the header height to adjust scroll position
          const header = document.querySelector('header');
          const headerHeight = header ? header.offsetHeight : 80; // Default to 80px if header not found
          
          if (sectionId) {
            const element = document.getElementById(sectionId);
            if (element) {
              // Scroll with header offset
              const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
              window.scrollTo({
                top: elementPosition - headerHeight - 16, // Additional 16px buffer
                behavior: 'smooth'
              });
            }
          } else if (selectedResult.scrollTo) {
            // If there's a specific section to scroll to (even without ID)
            const sectionElement = document.querySelector(selectedResult.scrollTo);
            if (sectionElement) {
              const elementPosition = sectionElement.getBoundingClientRect().top + window.pageYOffset;
              window.scrollTo({
                top: elementPosition - headerHeight - 16, // Additional 16px buffer
                behavior: 'smooth'
              });
            }
          } else if (selectedResult.category.includes('Tab')) {
            // For tab navigation in keyboard selection
            let tabId = '';
            
            // Determine tabs for different pages
            if (selectedResult.category.includes('About')) {
              if (selectedResult.title.includes('About Rishihood')) tabId = 'about-rishihood';
              else if (selectedResult.title.includes('About CHS')) tabId = 'about-chs';
              else if (selectedResult.title.includes('Vision')) tabId = 'vision-mission';
              else if (selectedResult.title.includes('Team')) tabId = 'team';
              else if (selectedResult.title.includes('Advisory')) tabId = 'advisory';
              else if (selectedResult.title.includes('Research')) tabId = 'research-committee';
              else if (selectedResult.title.includes('Internship')) tabId = 'internship-committee';
              else if (selectedResult.title.includes('Director')) tabId = 'directors-note';
            } 
            else if (selectedResult.category.includes('Events')) {
              if (selectedResult.title.includes('Conference')) tabId = 'conferences';
              else if (selectedResult.title.includes('Centre')) tabId = 'centre';
              else if (selectedResult.title.includes('Media')) tabId = 'media';
            }
            else if (selectedResult.category.includes('Publications')) {
              if (selectedResult.title.includes('Journal')) tabId = 'journals';
              else if (selectedResult.title.includes('Report')) tabId = 'reports';
              else if (selectedResult.title.includes('Book')) tabId = 'books';
            }
            
            // Navigate with hash
            if (tabId) {
              console.log('Navigating to tab with hash:', tabId);
              navigate(`${path}#${tabId}`);
            } else {
              navigate(path);
            }
          } else if (selectedResult.category.includes('Page Section')) {
            // For home page sections, try to find by ID first (most reliable)
            if (selectedResult.category.includes('Home')) {
              const sectionId = selectedResult.id.toString().replace(/-section$/, '');
              setTimeout(() => {
                // Try various ways to identify the section:
                // 1. By ID
                const sectionElement = document.getElementById(sectionId);
                if (sectionElement) {
                  const elementPosition = sectionElement.getBoundingClientRect().top + window.pageYOffset;
                  window.scrollTo({
                    top: elementPosition - headerHeight - 16,
                    behavior: 'smooth'
                  });
                  return;
                }
                
                // 2. By section with a specific class
                const sections = document.querySelectorAll('section');
                for (const section of Array.from(sections)) {
                  if (section.classList.contains(sectionId) || 
                      section.id === sectionId || 
                      section.querySelector(`h2, h3, .section-title`)?.textContent?.includes(selectedResult.title)) {
                    const elementPosition = section.getBoundingClientRect().top + window.pageYOffset;
                    window.scrollTo({
                      top: elementPosition - headerHeight - 16,
                      behavior: 'smooth'
                    });
                    return;
                  }
                }
                
                // 3. Fallback - find by heading text
                const headings = Array.from(document.querySelectorAll('h1, h2, h3, h4, .section-title'));
                const matchingHeading = headings.find(h => h.textContent?.includes(selectedResult.title));
                if (matchingHeading) {
                  const elementPosition = matchingHeading.getBoundingClientRect().top + window.pageYOffset;
                  window.scrollTo({
                    top: elementPosition - headerHeight - 16,
                    behavior: 'smooth'
                  });
                }
              }, 400); // Additional delay for home page sections
            } else {
              // For other pages' sections, find by heading text
              const headings = Array.from(document.querySelectorAll('h1, h2, h3, h4, .section-title'));
              const matchingHeading = headings.find(h => h.textContent?.includes(selectedResult.title));
              if (matchingHeading) {
                const elementPosition = matchingHeading.getBoundingClientRect().top + window.pageYOffset;
                window.scrollTo({
                  top: elementPosition - headerHeight - 16,
                  behavior: 'smooth'
                });
              }
            }
          }
        }, 400); // Increased delay to ensure navigation has completed
      }
    }
  };

  // Handle background click to close
  const handleBackgroundClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      closeSearch();
    }
  };

  if (!isSearchOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 md:p-6 lg:p-8"
      onClick={handleBackgroundClick}
    >
      <div 
        className="w-full max-w-2xl bg-white rounded-lg shadow-2xl overflow-hidden mt-16"
        onClick={e => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="relative border-b">
          <div className="flex items-center h-16 px-4">
            <Search className="h-5 w-5 text-gray-400 mr-3" />
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search across the entire site..."
              className="flex-1 outline-none text-lg font-medium"
              autoComplete="off"
            />
            {loading && <Loader2 className="h-5 w-5 text-gray-400 animate-spin mr-2" />}
            <button 
              onClick={closeSearch}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Search results */}
        <div 
          ref={resultsContainerRef}
          className="max-h-[70vh] overflow-y-auto"
        >
          {searchQuery.trim() === '' ? (
            <div className="py-8 px-6 text-center">
              <p className="text-gray-500">Start typing to search...</p>
            </div>
          ) : searchResults.length === 0 ? (
            <div className="py-8 px-6 text-center">
              <p className="text-gray-500">No results found for "{searchQuery}"</p>
            </div>
          ) : (
            <div className="p-2">
              {searchResults.map((result, index) => (
                <SearchResultItem
                  key={`${result.category}-${result.id}`}
                  result={result}
                  isSelected={index === selectedIndex}
                  ref={index === selectedIndex ? selectedResultRef : null}
                  onClick={() => closeSearch()}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-4 py-3">
          <div className="flex justify-between items-center text-sm text-gray-500">
            <div className="flex gap-3">
              <span className="flex items-center">
                <span className="mr-1">↑</span> <span className="mr-1">↓</span> to navigate
              </span>
              <span className="flex items-center">
                <span className="mr-1">↵</span> to select
              </span>
              <span className="flex items-center">
                <span className="mr-1">Esc</span> to close
              </span>
            </div>
            <div>
              {searchResults.length > 0 && `${searchResults.length} result${searchResults.length !== 1 ? 's' : ''}`}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Search result item component
interface SearchResultItemProps {
  result: SearchResult;
  isSelected: boolean;
  onClick: () => void;
}

const SearchResultItem = React.forwardRef<HTMLAnchorElement, SearchResultItemProps>(
  ({ result, isSelected, onClick }, ref) => {
    const [, navigate] = useLocation();

    // Handle navigation and scrolling to content
    const handleResultClick = (e: React.MouseEvent) => {
      e.preventDefault();
      onClick(); // Close the search modal

      // Check if it's an external link
      if (result.isExternalLink) {
        // Open external link in a new tab
        window.open(result.url, '_blank', 'noopener,noreferrer');
        return;
      }

      // Handle internal navigation
      // Parse the URL to extract path and possible section ID or hash
      const url = new URL(result.url, window.location.origin);
      const path = url.pathname;
      const hash = url.hash ? url.hash.substring(1) : null;
      
      // Special case for tabs - use direct URL with hash
      if (result.category.includes('Tab')) {
        if (hash) {
          // Navigate directly with the hash included
          console.log('Navigating to tab URL with hash:', `${path}#${hash}`);
          navigate(`${path}#${hash}`);
        } else {
          // Try to determine a hash from the tab title
          let tabId = '';
          
          if (result.category.includes('About')) {
            if (result.title.includes('About Rishihood')) tabId = 'about-rishihood';
            else if (result.title.includes('About CHS')) tabId = 'about-chs'; 
            else if (result.title.includes('Vision')) tabId = 'vision-mission';
            else if (result.title.includes('Team')) tabId = 'team';
            else if (result.title.includes('Advisory')) tabId = 'advisory';
            else if (result.title.includes('Research Committee')) tabId = 'research-committee';
            else if (result.title.includes('Internship Committee')) tabId = 'internship-committee';
            else if (result.title.includes('Director')) tabId = 'directors-note';
          } 
          else if (result.category.includes('Events')) {
            if (result.title.includes('Conference')) tabId = 'conferences';
            else if (result.title.includes('Centre')) tabId = 'centre';
            else if (result.title.includes('Media')) tabId = 'media';
          }
          else if (result.category.includes('Publications')) {
            if (result.title.includes('Journal')) tabId = 'journals';
            else if (result.title.includes('Report')) tabId = 'reports';
            else if (result.title.includes('Book')) tabId = 'books';
          }
          
          if (tabId) {
            // Navigate with the hash
            console.log('Navigating to tab with derived hash:', `${path}#${tabId}`);
            navigate(`${path}#${tabId}`);
          } else {
            // Fallback to regular navigation
            navigate(path);
          }
        }
        return;
      }
      
      // For non-tab navigation
      if (hash) {
        // Use the full path including hash
        navigate(`${path}#${hash}`);
      } else {
        // Regular navigation
        navigate(path);
      }

      // After navigation, handle scrolling
      setTimeout(() => {
        // Get the header height to adjust scroll position
        const header = document.querySelector('header');
        const headerHeight = header ? header.offsetHeight : 80; // Default to 80px if header not found
        
        if (hash) {
          const element = document.getElementById(hash);
          if (element) {
            // Scroll with header offset
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
            window.scrollTo({
              top: elementPosition - headerHeight - 16, // Additional 16px buffer
              behavior: 'smooth'
            });
          }
        } else if (result.scrollTo) {
          // If there's a specific section to scroll to (even without ID)
          const sectionElement = document.querySelector(result.scrollTo);
          if (sectionElement) {
            const elementPosition = sectionElement.getBoundingClientRect().top + window.pageYOffset;
            window.scrollTo({
              top: elementPosition - headerHeight - 16, // Additional 16px buffer
              behavior: 'smooth'
            });
          }
        } else if (result.category.includes('Page Section')) {
          // For home page sections, try to find by ID first (most reliable)
          if (result.category.includes('Home')) {
            const sectionId = result.id.toString().replace(/-section$/, '');
            setTimeout(() => {
              // Try various ways to identify the section:
              // 1. By ID
              const sectionElement = document.getElementById(sectionId);
              if (sectionElement) {
                const elementPosition = sectionElement.getBoundingClientRect().top + window.pageYOffset;
                window.scrollTo({
                  top: elementPosition - headerHeight - 16,
                  behavior: 'smooth'
                });
                return;
              }
              
              // 2. By section with a specific class
              const sections = document.querySelectorAll('section');
              for (const section of Array.from(sections)) {
                if (section.classList.contains(sectionId) || 
                    section.id === sectionId || 
                    section.querySelector(`h2, h3, .section-title`)?.textContent?.includes(result.title)) {
                  const elementPosition = section.getBoundingClientRect().top + window.pageYOffset;
                  window.scrollTo({
                    top: elementPosition - headerHeight - 16,
                    behavior: 'smooth'
                  });
                  return;
                }
              }
              
              // 3. Fallback - find by heading text
              const headings = Array.from(document.querySelectorAll('h1, h2, h3, h4, .section-title'));
              const matchingHeading = headings.find(h => h.textContent?.includes(result.title));
              if (matchingHeading) {
                const elementPosition = matchingHeading.getBoundingClientRect().top + window.pageYOffset;
                window.scrollTo({
                  top: elementPosition - headerHeight - 16,
                  behavior: 'smooth'
                });
              }
            }, 400); // Additional delay for home page sections
          } else {
            // For other pages' sections, find by heading text
            const headings = Array.from(document.querySelectorAll('h1, h2, h3, h4, .section-title'));
            const matchingHeading = headings.find(h => h.textContent?.includes(result.title));
            if (matchingHeading) {
              const elementPosition = matchingHeading.getBoundingClientRect().top + window.pageYOffset;
              window.scrollTo({
                top: elementPosition - headerHeight - 16,
                behavior: 'smooth'
              });
            }
          }
        }
      }, 400); // Increased delay to ensure navigation has completed
    };

    return (
      <a
        ref={ref}
        href={result.url}
        className={cn(
          "block px-4 py-3 rounded-md transition-colors hover:bg-gray-50 cursor-pointer",
          isSelected && "bg-gray-50"
        )}
        onClick={handleResultClick}
      >
        <div className="flex justify-between items-start">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1">
              <div className="text-base font-medium text-[#333A3D] truncate">{result.title}</div>
              {result.isExternalLink && (
                <span className="inline-flex text-gray-400" title="External link">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                    <polyline points="15 3 21 3 21 9"></polyline>
                    <line x1="10" y1="14" x2="21" y2="3"></line>
                  </svg>
                </span>
              )}
            </div>
            {result.description && (
              <div className="text-sm text-gray-500 mt-0.5 line-clamp-1">
                {result.description}
              </div>
            )}
            <div className="flex items-center gap-2 mt-1.5">
              <span className={cn(
                "px-2 py-0.5 text-xs font-medium rounded-full",
                result.category.includes("Publication") && "bg-blue-100 text-blue-800",
                result.category === "Blog" && "bg-green-100 text-green-800",
                result.category === "Event" && "bg-purple-100 text-purple-800",
                result.category.includes("Lecture") && "bg-yellow-100 text-yellow-800",
                result.category === "Research" && "bg-indigo-100 text-indigo-800",
                result.category === "PhD Program" && "bg-red-100 text-red-800",
                result.category === "Course" && "bg-orange-100 text-orange-800",
                result.category === "Page" && "bg-gray-100 text-gray-800",
                result.category === "Current Page" && "bg-amber-100 text-amber-800",
                result.category.includes("Page Section") && "bg-lime-100 text-lime-800",
                result.category.includes("Tab") && "bg-fuchsia-100 text-fuchsia-800"
              )}>
                {result.category}
              </span>
              {result.date && (
                <span className="text-xs text-gray-500">
                  {result.date}
                </span>
              )}
              {result.isExternalLink && (
                <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                  External Link
                </span>
              )}
            </div>
          </div>
          <ArrowRight 
            className={cn(
              "h-5 w-5 mt-1 text-gray-400",
              isSelected && "text-[#CC0033]"
            )}
          />
        </div>
      </a>
    );
  }
);

SearchResultItem.displayName = "SearchResultItem";

export default SearchModal;